import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { joiResolver } from "@hookform/resolvers/joi";
import * as Joi from "joi";
import { useForm } from "react-hook-form";
import {
  historyMessageCardFormProps,
  INormalizedLmcDataType,
  lmcOptionComponentProps,
} from "./types";
import LmcUtil from "./util";
import LMCButtons from "../../../public/LMCButtons";
import { CustomInput } from "../../../atoms/CustomInput";
import parse from "html-react-parser";

const createSchema = (t: any) =>
  Joi.object()
    .keys({
      historyEventNumber: Joi.number()
        .integer()
        .positive()
        .min(1)
        .max(100)
        .required()
        .label(
          t(
            "dashboard.features.lmc.historyMessageCard.validation.historyEventNumber"
          )
        )
        .messages({
          "number.base": t(
            "dashboard.features.lmc.historyMessageCard.validation.historyEventNumberRequired"
          ),
        }),
      comment: Joi.string()
        .max(100)
        .optional()
        .allow("", null)
        .label(
          t("dashboard.features.lmc.historyMessageCard.validation.comment")
        )
        .messages({
          "string.max": t(
            "dashboard.features.lmc.incrementalCard.validation.commentMax"
          ),
        }),
    })
    .required();

const HistoryMessageCard = ({
  label,
  optionData,
  onSubmitCardData,
  playgroundDataHandler,
  handleCancelEdit,
  tutorial,
}: lmcOptionComponentProps) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    control,
    getValues,
  } = useForm<historyMessageCardFormProps>({
    resolver: joiResolver(createSchema(t)),
    mode: "all",
  });

  useEffect(() => {
    if (optionData) {
      let denormalizedData = LmcUtil.unwrapper(
        "HIST_MESSAGE_CARD",
        optionData
      ) as historyMessageCardFormProps;

      reset({ ...denormalizedData });
    }
  }, [optionData, reset]);

  const onSubmit = (data: historyMessageCardFormProps) => {
    const normalizedData = LmcUtil.wrapper(
      "HIST_MESSAGE_CARD",
      data
    ) as INormalizedLmcDataType;

    onSubmitCardData("HIST_MESSAGE_CARD", normalizedData).then(() => {
      reset();
    });
  };

  useEffect(() => {
    playgroundDataHandler({
      type: "HIST_MESSAGE_CARD",
      data: LmcUtil.wrapper(
        "HIST_MESSAGE_CARD",
        getValues()
      ) as INormalizedLmcDataType,
    });

    watch((value, { name, type }) => {
      playgroundDataHandler({
        type: "HIST_MESSAGE_CARD",
        data: LmcUtil.wrapper(
          "HIST_MESSAGE_CARD",
          value
        ) as INormalizedLmcDataType,
      });
    });
  }, [getValues, playgroundDataHandler, watch]);

  return (
    <div className="lmc-card point-card w-full">
      <form onSubmit={handleSubmit(onSubmit)} className="w-full space-y-6">
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <div className="mb-5">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center w-7 h-7 bg-blue-500 text-white rounded-lg text-sm font-semibold shadow-sm">
                1
              </span>
              {t("dashboard.features.lmc.historyMessageCard.sectionTitle")}
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed mb-4">
              {t("dashboard.features.lmc.historyMessageCard.sectionDesc")}
            </p>

            <div className="bg-white p-4 rounded-lg border border-blue-300 space-y-2">
              <p className="text-sm font-medium text-gray-900 mb-2">
                {t(
                  "dashboard.features.lmc.historyMessageCard.historyTypesTitle"
                )}
              </p>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <svg
                    className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {t(
                        "dashboard.features.lmc.historyMessageCard.publicHistoryTitle"
                      )}
                    </p>
                    <p className="text-xs text-gray-600">
                      {t(
                        "dashboard.features.lmc.historyMessageCard.publicHistoryDesc"
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <svg
                    className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {t(
                        "dashboard.features.lmc.historyMessageCard.privateHistoryTitle"
                      )}
                    </p>
                    <p className="text-xs text-gray-600">
                      {t(
                        "dashboard.features.lmc.historyMessageCard.privateHistoryDesc"
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <CustomInput
              control={control}
              type="number"
              name="historyEventNumber"
              label={
                parse(
                  t("dashboard.features.lmc.historyMessageCard.label1")
                ) as string
              }
              error={errors.historyEventNumber}
              placeholder={
                t(
                  "dashboard.features.lmc.historyMessageCard.placeholder1"
                ) as string
              }
              min={1}
              max={100}
              onWheel={(e) => e.currentTarget.blur()}
              helperText={errors.historyEventNumber?.message}
            />

            <CustomInput
              rows={4}
              name="comment"
              control={control}
              error={errors.comment}
              helperText={errors.comment?.message}
              placeholder={
                t(
                  "dashboard.features.lmc.historyMessageCard.placeholder2"
                ) as string
              }
              label={
                t("dashboard.features.lmc.historyMessageCard.label2") as string
              }
            />
          </div>
        </div>

        <div className="pt-2">
          <LMCButtons tutorial={tutorial} handleCancelEdit={handleCancelEdit} />
        </div>
      </form>
    </div>
  );
};

export const HistoryMessageCardUi = ({
  data,
}: {
  data: INormalizedLmcDataType | null;
}) => {
  const { t } = useTranslation();

  const samples = () => {
    return [
      {
        title: t("dashboard.features.lmc.help.historyCard.sampleHistory1"),
        date: "October 15, 2024",
      },
      {
        title: t("dashboard.features.lmc.help.historyCard.sampleHistory2"),
        date: "September 8, 2024",
      },
      {
        title: t("dashboard.features.lmc.help.historyCard.sampleHistory3"),
        date: "August 22, 2024",
      },
    ];
  };

  return (
    <div className="self-stretch  flex-col justify-start items-center gap-6 flex">
      <div className="self-stretch  bg-gray-800 rounded-xl shadow flex-col justify-start items-start flex py-4">
        <div className="px-4 w-full justify-between items-start inline-flex pb-3 mb-3">
          <div className="text-center text-gray-400 text-base font-bold font-figtree leading-normal">
            {t("dashboard.features.lmc.historyMessageCard.key1")}
          </div>
          <div className="text-center text-red-400 text-base font-regular font-figtree leading-normal">
            {t("dashboard.features.lmc.historyMessageCard.key2")}
          </div>
        </div>
        <div className="self-stretch">
          {data &&
            samples()?.map((item, index) => (
              <div
                key={index}
                className={`px-4 border-b border-gray-700 pb-3 ${
                  5 !== index ? "mb-3" : "mb-2"
                }`}
              >
                <div className="self-stretch font-regular text-base text-gray-200 mb-2">
                  {item.title}
                </div>
                <div className="text-gray-400 text-sm font-regular font-figtree leading-normal">
                  {item.date}
                </div>
              </div>
            ))}
        </div>

        <div className="px-4 text-blue-400 text-base font-regular font-figtree leading-normal">
          {t("dashboard.features.lmc.general.seeAll")}
        </div>
      </div>
    </div>
  );
};

export default HistoryMessageCard;
